import {
  Affirmation,
  CategoryId,
  ContentSection,
  DatePattern,
  NumberEntry,
  NumberType,
} from '../types/angelNumber';
import { DigitTheme, theme } from './digitThemes';

type Narrative = 'master' | 'portal' | 'mirror' | 'hour' | 'sequence' | 'guidance' | 'special' | 'date';

const narrativeByType: Record<NumberType, Narrative> = {
  angel_number: 'guidance',
  portal_number: 'portal',
  mirror_number: 'mirror',
  mirror_hour: 'hour',
  master_number: 'master',
  sequential_number: 'sequence',
  special_sequence: 'special',
  portal_date: 'date',
};

const typeCategories: Record<NumberType, CategoryId[]> = {
  angel_number: ['angel'],
  portal_number: ['portal'],
  mirror_number: ['mirror', 'reflection'],
  mirror_hour: ['mirror', 'time'],
  master_number: ['master'],
  sequential_number: ['sequence'],
  special_sequence: ['angel', 'reflection'],
  portal_date: ['portal', 'reflection'],
};

export interface ComposeInput {
  id: string;
  /** Normalized numeric form */
  number: string;
  displayNumber: string;
  type: NumberType;
  title: string;
  /** Dominant digit */
  a: number;
  /** Supporting digit (defaults to the dominant one) */
  b?: number;
  searchAliases?: string[];
  keywords?: string[];
  extraCategories?: CategoryId[];
  /** For sequential numbers: the ordered digits, e.g. [1,2,3,4] */
  steps?: number[];
  popularityScore?: number;
  isFeatured?: boolean;
  datePattern?: DatePattern;
}

const uniq = <T,>(arr: T[]): T[] => Array.from(new Set(arr));

export function slugify(displayNumber: string, title: string): string {
  const numberPart = displayNumber.replace(/[^0-9a-z]+/gi, '-').replace(/^-|-$/g, '');
  const titlePart = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return `${numberPart}-${titlePart}`;
}

export function section(title: string, content: string): ContentSection {
  return { title, content };
}

function shortMeaning(n: Narrative, A: DigitTheme, B: DigitTheme): string {
  const pair = A.digit === B.digit ? A.keyword : `${A.keyword} and ${B.keyword}`;
  switch (n) {
    case 'portal':
      return `Often interpreted as a symbolic doorway — a moment to pause and notice ${pair}.`;
    case 'hour':
      return `A symbolic moment of reflection, associated with ${pair}.`;
    case 'mirror':
      return `A mirror pattern, often used as a prompt to look honestly at ${pair}.`;
    case 'sequence':
      return `Symbolically associated with steady progress, one step at a time.`;
    case 'master':
      return `An amplified expression of ${A.keyword}, often read as emphasis rather than instruction.`;
    case 'date':
      return `A symbolic date many people use for reflection and intention-setting around ${pair}.`;
    case 'special':
      return `Often interpreted as a conversation between ${pair}.`;
    default:
      return `A gentle symbolic message, associated with ${pair}.`;
  }
}

function intro(display: string, n: Narrative, A: DigitTheme, B: DigitTheme): string {
  switch (n) {
    case 'master':
      return `${display} is often described as a master number: a single theme turned up in volume. Symbolically it carries the energy of ${A.keyword}, felt more intensely than usual.`;
    case 'portal':
      return `Some spiritual traditions describe ${display} as a portal number — a symbolic doorway rather than a literal one. Many people treat it as an invitation to slow down and pay closer attention to ${A.keyword}.`;
    case 'mirror':
      return `${display} reads the same forwards and backwards, which is why it is often interpreted as a mirror: a nudge to notice what ${A.keyword} is quietly reflecting back to you.`;
    case 'hour':
      return `Noticing ${display} on a clock is often experienced as a small pause in the day. Symbolically, this hour is associated with ${A.keyword}.`;
    case 'sequence':
      return `${display} climbs one step at a time, and is often read as a symbol of steady progress — a path that begins with ${A.keyword} and moves toward ${B.keyword}.`;
    case 'date':
      return `${display} is a date that some spiritual communities associate with reflection and intention-setting. Symbolically it is linked to ${A.keyword}.`;
    case 'special':
      return `${display} is often read as a quiet conversation between two energies — ${A.keyword} and ${B.keyword}.`;
    default:
      return `${display} is often interpreted as a short piece of gentle guidance: ${A.keyword}, held steady by ${B.keyword}.`;
  }
}

function core(display: string, n: Narrative, A: DigitTheme, B: DigitTheme, steps?: number[]): string {
  const same = A.digit === B.digit;
  const blend = same
    ? `Symbolically, ${display} concentrates one idea: ${A.essence}.`
    : `Symbolically, ${display} blends two ideas: ${A.essence}, softened by ${B.essence}.`;

  switch (n) {
    case 'master':
      return `${blend} Many people notice master numbers during periods when a decision feels larger than usual — not because the number decides anything, but because it draws attention to what you are already weighing up. You may read it as emphasis rather than instruction.`;
    case 'portal':
      return `${blend} In spiritual circles, a repeating pattern like this is often described as a moment of heightened intention: a symbolic opening where awareness, rather than circumstance, is what shifts. Many people use it as a cue to name what they actually want next.`;
    case 'mirror':
      return `${blend} Mirror patterns are often interpreted as a prompt for self-reflection — a suggestion to look at the loops you tend to repeat, and to ask which of them still serve you. Nothing here is a verdict; it is an invitation to look honestly and kindly.`;
    case 'hour':
      return `${blend} Because a mirror hour appears and passes within a minute, many people treat it as a small built-in reminder: one breath, one honest check-in, then back to the day.`;
    case 'sequence':
      return `${blend} Ascending sequences are often read as a symbol of progression — the idea that you do not need the whole staircase, only the next step. ${
        steps && steps.length > 3
          ? 'A longer run like this is sometimes taken as a reminder that the change you are in is gradual by design.'
          : 'It is frequently noticed by people who are mid-transition and impatient for the outcome.'
      }`;
    case 'date':
      return `${blend} Some spiritual traditions connect repeating dates with symbolic “portal energy” — not a supernatural event, but a shared moment when many people choose to reflect at the same time. You may use it as a natural marker in the calendar for setting an intention.`;
    case 'special':
      return `${blend} Patterns that alternate like this are often interpreted as a request for integration: letting two parts of your life inform each other instead of competing for the same hours.`;
    default:
      return `${blend} Numbers like this are often described as angelic guidance — not a message with instructions, but a reminder that support tends to arrive in small, ordinary forms. Many people read it as encouragement to keep going in the direction they had already sensed.`;
  }
}

function love(A: DigitTheme, B: DigitTheme): string {
  return A.digit === B.digit
    ? `In love, this number is often associated with ${A.love}. You may use it as a reminder to be clear and kind about what you need, rather than hoping it will be guessed.`
    : `In love, this pattern is often associated with ${A.love}, balanced by ${B.love}. Symbolically it points to connection that grows through honesty rather than intensity.`;
}

function career(A: DigitTheme, B: DigitTheme): string {
  return A.digit === B.digit
    ? `In work and money, this number is often linked to ${A.career}. Many people take it as a prompt to look at one practical next step rather than the entire plan.`
    : `In work and money, this pattern is often linked to ${A.career}, tempered by ${B.career}. Read symbolically, it suggests progress that is more sustainable than dramatic.`;
}

function spiritual(n: Narrative, A: DigitTheme, B: DigitTheme): string {
  const base =
    A.digit === B.digit
      ? `Spiritually, this number is often interpreted as ${A.spiritual}.`
      : `Spiritually, this number is often interpreted as ${A.spiritual}, alongside ${B.spiritual}.`;
  const tail =
    n === 'portal' || n === 'hour' || n === 'date'
      ? ' Some spiritual traditions treat moments like this as symbolic rather than predictive: the meaning comes from the attention you bring to it.'
      : ' Some traditions describe this as guidance; others simply as a useful mirror. Either way, the reflection it prompts is yours.';
  return base + tail;
}

function portal(display: string, n: Narrative, A: DigitTheme): string {
  if (n === 'date') {
    return `“Portal” here is a symbolic idea, not a supernatural claim. Some spiritual communities describe ${display} as a day with heightened symbolic significance around ${A.keyword}. Many people use it as a shared moment to reflect, set an intention or begin something small.`;
  }
  return `“Portal” here is a symbolic idea, not a supernatural claim. Some spiritual communities describe ${display} as a threshold moment — a point where ${A.keyword} feels more available to notice. You may use it as a marker: a moment to set an intention, close something gently, or acknowledge that a chapter is turning.`;
}

function prompts(display: string, n: Narrative, A: DigitTheme, B: DigitTheme): string[] {
  const byNarrative: Record<Narrative, string> = {
    master: 'If this theme were turned up in your life on purpose, what would you do differently this week?',
    portal: 'What intention would you like to set while this feels like a threshold?',
    mirror: 'What pattern keeps repeating for you, and what is it reflecting?',
    hour: 'What is one small thing you could set down before the day continues?',
    sequence: 'What is the very next step — not the whole plan, just the next one?',
    guidance: 'Where in your life would you like to feel more supported?',
    special: 'Which two parts of your life are asking to be balanced right now?',
    date: 'What would you like this date to mark for you?',
  };

  return uniq([
    `Pause and notice what you were thinking about when you saw ${display}.`,
    A.reflect,
    byNarrative[n],
    B.digit !== A.digit ? B.reflect : `What would ${A.short.toLowerCase()} look like in practice tomorrow?`,
  ]).slice(0, 4);
}

export function composeEntry(input: ComposeInput): NumberEntry {
  const A = theme(input.a);
  const B = theme(input.b ?? input.a);
  const n = narrativeByType[input.type];
  const display = input.displayNumber;

  const categories: CategoryId[] = uniq([
    ...typeCategories[input.type],
    ...A.categories,
    ...B.categories.slice(0, 1),
    ...(input.extraCategories ?? []),
  ]);

  const affirmation: Affirmation = { text: A.affirmation };
  const score = input.popularityScore ?? 40;

  return {
    id: input.id,
    number: input.number,
    displayNumber: display,
    slug: slugify(display, input.title),
    type: input.type,
    categories,
    title: input.title,
    shortMeaning: shortMeaning(n, A, B),
    coreMeaning: section('Core Meaning', `${intro(display, n, A, B)} ${core(display, n, A, B, input.steps)}`),
    loveMeaning: section('Love', love(A, B)),
    careerMeaning: section('Career & Money', career(A, B)),
    spiritualMeaning: section('Spiritual Meaning', spiritual(n, A, B)),
    portalMeaning:
      n === 'portal' || n === 'hour' || n === 'date'
        ? section('Portal Symbolism', portal(display, n, A))
        : undefined,
    affirmation,
    reflectionPrompts: prompts(display, n, A, B),
    keywords: uniq([...A.searchWords, ...B.searchWords, ...(input.keywords ?? [])]),
    relatedNumbers: [],
    isPopular: score >= 70,
    isFeatured: input.isFeatured ?? false,
    popularityScore: score,
    searchAliases: uniq([input.number, display, ...(input.searchAliases ?? [])]),
    metadata: {
      energy: A.metadata.energy,
      theme: A.metadata.theme,
      element: A.metadata.element,
    },
    datePattern: input.datePattern,
    interpretationType: 'symbolic_spiritual',
  };
}
